import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import {SCButton,
  SCCheckbox,
  SCInput,
  SCSelect,
  SCSwitch, SCStepper} from 'rollup-allspark';
import {
  DOCKET_PRINTER_TYPES,
  PRINTER_FONT_SIZES,
  PRINTER_ROLLS,
} from '../../../config/constants/printerProfiles';
import usePrinter from '../../../hooks/usePrinter';
import { cn, isEmpty } from '../../../utils';
import { unmapProfile } from '../../../utils/printerProfile';
import SelectionRadio from '../SelectionRadio';
import { getList, saveEntity, updateEntity } from '../actions';
import { PRINTER_PROFILE_SAVE } from '../sagaMap';
import { makeSelectProperty } from '../selector';

const General = ({
  getPrinterProfiles,
  isSaving,
  selectedBranch,
  selectedProfile,
  saveProfile,
  updateField,
}) => {
  const [profile, setProfile] = useState(selectedProfile);

  const { getProfiles } = usePrinter({
    getPrinterProfiles,
    selectedBranch,
    updateField,
  });

  useEffect(() => {
    setProfile({ ...selectedProfile, sticker_printer_enabled: false });
  }, [selectedProfile]);

  const onPrinterTypeClicked = pType => {
    const childType = !isEmpty(pType.childTypes) ? pType.childTypes[0] : null;
    const mappedProfile = {
      ...profile,
      printerType: pType,
      printerChildType: childType,
    };

    setProfile(mappedProfile);
  };

  const onSaveClicked = () => {
    const unMappedProfile = unmapProfile(profile);
    const data = { ...unMappedProfile, branch_id: selectedBranch.id };

    saveProfile({
      ...PRINTER_PROFILE_SAVE,
      data,
      callbackFn: () => {
        getProfiles(selectedBranch);

        updateField({
          selectedProfile: profile,
        });
      },
    });
  };

  const defaultEl = profile?.is_default ? (
    <div className="bg-grey-light rounded-lg p-4 flex gap-x-2">
      <i className="kt-info text-lg" />
      <div className="flex flex-col gap-y-2">
        <div className="text-base">This is the default printer profile</div>
        <div className="text-sm text-grey-darker">
          Default printer profile prints all the items without routing attached.
        </div>
      </div>
    </div>
  ) : null;

  const printerTypeEl = (() => {
    const listEl = DOCKET_PRINTER_TYPES.map(pType => {
      const childEl = (() => {
        if (isEmpty(pType.childTypes)) {
          return null;
        }

        const childPrinterList = pType.childTypes.map(cType => {
          const isSelected =
            pType.id === profile?.printerType?.id &&
            cType.id === profile?.printerChildType?.id;

          return (
            <div
              className={cn({
                'flex flex-1 gap-x-2 px-4 py-2 border rounded-lg cursor-pointer': true,
                'border-primary-light bg-primary-lighter': isSelected,
                'border-grey': !isSelected,
              })}
              role="presentation"
              onClick={() =>
                setProfile({
                  ...profile,
                  printerType: pType,
                  printerChildType: cType,
                })
              }
            >
              <SelectionRadio isSelected={isSelected} />
              {cType.title}
            </div>
          );
        });

        return <div className="flex gap-x-2">{childPrinterList}</div>;
      })();

      return (
        <div className="py-2 flex flex-col gap-y-4">
          <div
            className="flex gap-x-2 items-center cursor-pointer"
            role="presentation"
            onClick={() => onPrinterTypeClicked(pType)}
          >
            <SelectionRadio
              isSelected={pType.id === profile?.printerType?.id}
            />
            {pType.title}
          </div>
          {childEl}
        </div>
      );
    });

    return (
      <div className="flex flex-col gap-y-2 mt-4 w-1/2">
        <div className="font-medium">Connection Type</div>
        {listEl}
      </div>
    );
  })();

  return (
    <div className="w-full flex flex-col gap-y-4">
      <SCInput
        label={
          <div className="font-medium text-sm text-black">Profile Name</div>
        }
        value={profile?.profile_name}
        handleChange={e =>
          setProfile({ ...profile, profile_name: e.target.value })
        }
        type="text"
        name="profile_name"
        inputClass="border rounded-lg"
        hasError={isEmpty(profile?.profile_name)}
        hint={isEmpty(profile?.profile_name) ? 'Profile name is required' : ''}
      />
      {defaultEl}
      <div className="flex w-full gap-x-8">
        {printerTypeEl}
        <div className="flex flex-col justify-between gap-y-6 pt-4 w-1/2">
          <div className="font-medium">Set Docket Type</div>
          <div className="flex items-center">
            <SCCheckbox
              value={profile.docket_customer_copy_enabled}
              handleChange={v =>
                setProfile({
                  ...profile,
                  docket_customer_copy_enabled: v,
                })
              }
            />
            <div className="flex gap-x-8">
              <span className="w-32 flex items-center leading-5">
                Customer Copy
              </span>
              <SCStepper
                value={profile.docket_customer_copy_count}
                max={5}
                min={1}
                step={1}
                variant="primary-outline"
                size="sm"
                disabled={!profile.docket_customer_copy_enabled}
                onChange={v =>
                  setProfile({
                    ...profile,
                    docket_customer_copy_count: v,
                  })
                }
              />
            </div>
          </div>
          <div className="flex items-center">
            <SCCheckbox
              value={profile.docket_kitchen_copy_enabled}
              handleChange={v =>
                setProfile({
                  ...profile,
                  docket_kitchen_copy_enabled: v,
                  docket_kitchen_copy_count:
                    !v || (v && profile.docket_kitchen_copy_count === 0)
                      ? 1
                      : profile.docket_kitchen_copy_count,
                })
              }
            />
            <div className="flex gap-x-8">
              <span className="w-32 flex items-center leading-5">
                Kitchen Copy
              </span>
              <SCStepper
                value={profile.docket_kitchen_copy_count}
                max={5}
                min={1}
                step={1}
                variant="primary-outline"
                size="sm"
                disabled={!profile.docket_kitchen_copy_enabled}
                onChange={v =>
                  setProfile({
                    ...profile,
                    docket_kitchen_copy_count: v,
                  })
                }
              />
            </div>
          </div>

          <div className="flex flex-col gap-y-2 mt-4">
            <div className="font-medium">Preferences</div>
            <div className="bg-grey-lighter rounded-lg p-4 flex justify-between">
              <div className="flex gap-x-3">
                <i className="kt-receipt text-xl flex items-center" />
                <div className="flex flex-col gap-y-1">
                  <div className="font-medium">Auto Print</div>
                  <div className="text-xs text-grey-darker">
                    Auto print docket when order is accepted
                  </div>
                </div>
              </div>

              <div className="flex items-center">
                <SCSwitch
                  value={profile.auto_print_enabled}
                  handleChange={v =>
                    setProfile({
                      ...profile,
                      auto_print_enabled: v,
                    })
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-full gap-x-4">
        <SCSelect
          options={PRINTER_ROLLS}
          value={profile?.rollType}
          title={
            <div className="font-medium text-sm text-black">
              Printer Paper Size
            </div>
          }
          label="title"
          trackBy="id"
          allowEmpty={false}
          onSelect={rollType => setProfile({ ...profile, rollType })}
        />
        <SCSelect
          options={PRINTER_FONT_SIZES}
          value={profile?.font}
          title={
            <div className="font-medium text-sm text-black">Font Size</div>
          }
          label="label"
          trackBy="id"
          allowEmpty={false}
          onSelect={font => setProfile({ ...profile, font })}
        />
      </div>

      <div className="flex items-end justify-end mt-4">
        <SCButton
          disabled={isSaving || isEmpty(profile?.profile_name)}
          label={isSaving ? 'Saving...' : 'Save'}
          action={onSaveClicked}
        />
      </div>
    </div>
  );
};

General.propTypes = {
  getPrinterProfiles: PropTypes.func,
  isSaving: PropTypes.bool,
  selectedBranch: PropTypes.object,
  selectedProfile: PropTypes.object,
  saveProfile: PropTypes.func,
  updateField: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  isSaving: makeSelectProperty('isSaving'),
  selectedBranch: makeSelectProperty('selectedBranch'),
  selectedProfile: makeSelectProperty('selectedProfile'),
});

const mapDispatchToProps = dispatch => ({
  getPrinterProfiles: payload => dispatch(getList(payload)),
  updateField: payload => dispatch(updateEntity(payload)),
  saveProfile: payload => dispatch(saveEntity(payload)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(General);
